import { useState, useCallback } from 'react';

export type ControlledExpandBehaviorType = {
  isExpanded: (id: string) => boolean;
  toggleExpandStatus: (id: string) => void;
};

/**
 * Hook to manage expanded/collapsed state of components.
 * Usage: in cases where a group of components are expected to
 * have only one expanded and the rest are collapsed.
 *
 * return value:
 *  isExpanded(id: string): boolean. Returns whether or not the id is
 *  the currently expanded component.
 *  toggleExpandStatus(id: string): void. Sets id to be the new expanded
 *  component.
 * @param param0
 */
export default function useControlledExpandBehavior({
  defaultExpanded,
}: {
  defaultExpanded: string;
}): ControlledExpandBehaviorType {
  const [currentExpanded, setCurrentExpanded] = useState<string | null>(
    defaultExpanded,
  );

  const isExpanded = useCallback(
    (id: string): boolean => id === currentExpanded,
    [currentExpanded],
  );

  const toggleExpandStatus = useCallback(
    (id: string): void => {
      // collapsing the currently expanded component set newExpanded to null to avoid collision
      const newExpanded: string | null = id === currentExpanded ? null : id;
      setCurrentExpanded(newExpanded);
    },
    [currentExpanded],
  );

  return {
    isExpanded,
    toggleExpandStatus,
  };
}
